import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { FileText, TrendingUp, ArrowRight } from "lucide-react";

const DATA = [
  { name: "Nur Calcium", value: 100 },
  { name: "+ Vitamin D", value: 128 },
  { name: "+ Vitamin K2", value: 152 },
  { name: "+ Probiotika", value: 176 },
];
const COLORS = ["#B9C9B7", "#769777", "#2D5A27", "#1E3F20"];

const STUDIES = [
  { metric: "P1NP", value: "+8,4 μg/L", label: "Knochenformation (Meta-Analyse, 15 RCTs)" },
  { metric: "CTX-I", value: "SMD −0,35", label: "Knochenresorption gesenkt (≈15–20 %)" },
  { metric: "Fraktur", value: "−20–30 %", label: "Risiko bei mediterraner Ernährung (Kohorten)" },
];

export default function Science() {
  return (
    <section id="wissenschaft" className="scroll-mt-24 py-20 lg:py-28 bg-[#1E3F20] text-white fb-grain">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mb-12">
          <span className="text-xs uppercase tracking-widest font-semibold text-[#D99B26]">Evidenz</span>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold tracking-tight text-white">
            Wissenschaft &amp; Studien
          </h2>
          <p className="mt-4 text-lg text-[#C7D5C4] leading-relaxed">
            Transparente Studienergebnisse zur Wirkung von Nährstoffen auf die Knochendichte. Die Grafik zeigt die
            relative Calcium-Verwertung mit ergänzenden Bausteinen (Index, nur Calcium = 100).
          </p>
          <Link
            to="/forschung"
            data-testid="science-research-link"
            className="mt-6 inline-flex items-center gap-2 bg-[#D99B26] hover:bg-[#c68b1e] text-[#1E3F20] font-semibold rounded-full px-6 py-3 transition-colors"
          >
            <FileText className="w-4 h-4" /> Zum Science Deep Dive <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="grid lg:grid-cols-5 gap-8 items-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            data-testid="science-chart-container"
            className="lg:col-span-3 bg-white/5 rounded-3xl ring-1 ring-white/10 p-6"
          >
            <div className="flex items-center gap-2 mb-4 text-[#D99B26]">
              <TrendingUp className="w-5 h-5" />
              <span className="text-sm font-semibold">Relative Calcium-Verwertung (Index)</span>
            </div>
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={DATA} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.12)" />
                <XAxis dataKey="name" tick={{ fill: "#C7D5C4", fontSize: 12 }} />
                <YAxis tick={{ fill: "#C7D5C4", fontSize: 12 }} />
                <Tooltip
                  cursor={{ fill: "rgba(255,255,255,0.06)" }}
                  contentStyle={{ background: "#163017", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 12, color: "#fff" }}
                />
                <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                  {DATA.map((_, i) => (
                    <Cell key={i} fill={COLORS[i]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </motion.div>

          <div className="lg:col-span-2 space-y-4">
            {STUDIES.map((s, i) => (
              <motion.div
                key={s.metric}
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="bg-white/5 rounded-2xl ring-1 ring-white/10 p-5"
              >
                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-xs uppercase tracking-widest text-[#8FA58C] font-semibold">{s.metric}</span>
                  <span className="font-display text-2xl font-bold text-[#D99B26]">{s.value}</span>
                </div>
                <p className="text-sm text-[#C7D5C4] mt-1">{s.label}</p>
              </motion.div>
            ))}
            <div className="flex items-start gap-3 text-xs text-[#8FA58C] pt-2">
              <FileText className="w-4 h-4 shrink-0 mt-0.5" />
              <p>Basierend auf aktuellen Meta-Analysen und systematischen Reviews (2025–2026). Platzhalter für weitere Grafiken &amp; Studien.</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
